import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { bridgeEventStreamAtom, daemonPushFrameStreamAtom } from '../stores/sdk/atoms';
import { SESSION_LIST_PAGES_KEY, SESSION_SEARCH_PAGES_KEY } from './useSessionList';

/**
 * Reconnect invalidation (sdk ADR-b2a2e):
 * when the event transport reconnects, every cache entry goes stale and heals
 * by refetch — never replay. The binding invalidates the render-facing
 * TanStack queries; the SDK cache entries stay paintable and revalidate on
 * their next read (evicting them would blank the return paint).
 */
export function useCacheReconnectInvalidation(): void {
  const queryClient = useQueryClient();
  const webStream = useAtomValue(bridgeEventStreamAtom);
  const daemonStream = useAtomValue(daemonPushFrameStreamAtom);
  const stream = webStream ?? daemonStream;

  useEffect(() => {
    if (!stream || !('onOpen' in stream)) return;
    const off = (stream as { onOpen: (handler: () => void) => () => void }).onOpen(() => {
      // Reconnect marks every entry stale (sdk ADR-b2a2e): the entries stay
      // paintable and the read path's unconditional revalidate heals them.
      // Evicting here blanks the next paint — the return leg flashes a spinner.
      void queryClient.invalidateQueries({ queryKey: SESSION_LIST_PAGES_KEY });
      void queryClient.invalidateQueries({ queryKey: SESSION_SEARCH_PAGES_KEY });
    });
    return off;
  }, [stream, queryClient]);
}
